import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import styled, { css, keyframes } from 'styled-components';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import eventTypes from 'theme/eventTypes';
import {
    Calendar,
    CalendarPlus,
    Check,
    ChevronRight,
    CreditCard,
    Gift,
    Heart,
    Info,
    MapPin,
    Navigation,
    Share2,
    Ticket,
    X,
    Zap,
} from 'lucide-react';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import DescriptionWithHTML from './DescriptionWithHTML';
import eventApi from 'api/requests/events';
import { hasEventEnded } from 'helpers/eventEnded';
import { ui, shortDate, shortTime, PLACEHOLDER_EVENT } from 'theme/discover';
import { useAuth } from 'context/auth/Auth';
import useFavorite from 'hooks/useFavorite';
import useShare from 'hooks/useShare';
import currency from 'helpers/currency';
import { buildOrganizers } from 'modules/events/page/cover/CoverOrganizers';

const AI_ORIGIN = 'kaue.ia';
const PLACEHOLDER = PLACEHOLDER_EVENT;

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

// Acima disso a descrição fecha com "Ler mais": a modal é uma prévia, o texto
// inteiro não deve empurrar local e organizador para longe.
const DESCRIPTION_CLAMP = 220;

function venueName(content) {
    const address = content?.address;

    return address?.name || address?.street?.name || content?.establishment?.name || 'Local a definir';
}

function venueCity(content) {
    const address = content?.address;
    const city = address?.city_name || address?.street?.city?.name;
    const state = address?.state_name || address?.street?.city?.state?.initials;

    return [city, state].filter(Boolean).join(', ');
}

/**
 * Endereço legível a partir do formato estruturado, caindo para `raw_address`
 * — os eventos mapeados por IA, por exemplo, só trazem o texto livre.
 */
function fullAddress(address) {
    if (!address) return null;

    const parts = [
        address?.street?.name,
        address?.number && `nº ${address.number}`,
        address?.street?.district && `Bairro ${address.street.district}`,
        address?.complement,
    ].filter(Boolean);

    return parts.length ? parts.join(', ') : (address?.raw_address || null);
}

function directionsUrl(content, address) {
    const lat = content?.address?.lat;
    const lng = content?.address?.long;

    if (lat && lng) return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    if (content?.link_map) return content.link_map;
    if (address) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

    return null;
}

/** A primeira data de hoje em diante, para o "Adicionar à agenda". */
function nextCalendarDay(dates) {
    const today = dayjs().format('YYYY-MM-DD');

    return (dates || []).find(item => String(item?.date || '').slice(0, 10) >= today) || null;
}

/**
 * Link do Google Agenda para um dia do evento. Sem horário vira evento de dia
 * inteiro; sem término, duas horas a partir do início.
 */
function calendarUrl({ title, day, location, details }) {
    const dayStr = String(day.date).slice(0, 10);
    let dates;

    if (day.start) {
        const start = dayjs(`${dayStr}T${day.start}`);
        let end = day.end ? dayjs(`${dayStr}T${day.end}`) : start.add(2, 'hour');
        // Término de madrugada (22h às 4h) cai no dia seguinte.
        if (!end.isAfter(start)) end = end.add(1, 'day');

        dates = `${start.format('YYYYMMDD[T]HHmmss')}/${end.format('YYYYMMDD[T]HHmmss')}`;
    } else {
        const start = dayjs(dayStr);
        dates = `${start.format('YYYYMMDD')}/${start.add(1, 'day').format('YYYYMMDD')}`;
    }

    const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: title || '',
        dates,
        ctz: 'America/Sao_Paulo',
    });

    if (location) params.set('location', location);
    if (details) params.set('details', details);

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export default function EventDetailV2({ data, fromHome = false, onClose }) {
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [linkCopied, setLinkCopied] = useState(false);
    const [showAiNote, setShowAiNote] = useState(false);
    const [descriptionOpen, setDescriptionOpen] = useState(false);
    const [descriptionLong, setDescriptionLong] = useState(false);
    const descriptionRef = useRef(null);

    const { isFavorited, toggleFavorite } = useFavorite(data?.slug, data?.is_favorited);
    const { share } = useShare();
    const { isAuth } = useAuth();

    useEffect(() => {
        getEvent(data?.slug);
    }, []);

    // Mede depois de pintar: só a altura real diz se a descrição passa do corte.
    // Depende de `loading` também: no React 17 o `setContent` e o `setLoading`
    // da busca renderizam separados, e no primeiro a tela ainda é o esqueleto.
    useEffect(() => {
        if (descriptionRef.current) {
            setDescriptionLong(descriptionRef.current.scrollHeight > DESCRIPTION_CLAMP + 40);
        }
    }, [content, loading]);

    async function getEvent(slug) {
        setLoading(true);
        try {
            const { data: eventData } = await eventApi.getBySlug(slug, fromHome ? { fromHome: true } : {});
            setContent(eventData?.data);
        } catch (error) {
            console.error('Error fetching event:', error);
        } finally {
            setLoading(false);
        }
    }

    function handleFavorite() {
        if (!isAuth && onClose) {
            onClose();
        }
        toggleFavorite();
    }

    const eventUrl = `${import.meta.env.VITE_CLIENT_URL}/evento/${data?.slug}`;

    async function handleShare() {
        const result = await share({
            title: content?.name,
            url: content?.link_ticket || eventUrl,
        });

        if (result?.method === 'clipboard' && result?.success) {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2500);
        }
    }

    if (loading) {
        return <DetailSkeleton onClose={onClose} />;
    }

    if (!content?.name) {
        return null;
    }

    const tickets = (content.products || []).filter(el => el.type === 'ticket');
    const products = (content.products || []).filter(el => el.type === 'product');
    const hasTickets = tickets.length > 0;
    const hasProducts = products.length > 0;
    const isPast = hasEventEnded(content);

    const isFree = content.classification?.name === eventTypes.free.slug;

    // `starting_price` é o menor preço à venda agora (a API já descarta o
    // esgotado e o oculto). Respostas sem o campo caem no menor preço da lista.
    const ticketPrices = tickets
        .map(item => Number(item?.price))
        .filter(price => Number.isFinite(price) && price > 0);
    const lowestPrice = content.starting_price != null && !Number.isNaN(Number(content.starting_price))
        ? Number(content.starting_price)
        : (ticketPrices.length ? Math.min(...ticketPrices) : null);
    const showsFree = isFree || lowestPrice === 0;

    const priceFact = isPast
        ? { title: 'Encerrado', note: 'Vendas encerradas' }
        : showsFree
            ? { title: 'Gratuito', note: isFree ? 'Entrada livre' : 'Ingresso gratuito' }
            : lowestPrice != null
                ? { title: `A partir de ${currency.format(lowestPrice)}`, note: 'Ingresso por faixa' }
                : { title: 'Pago', note: 'Veja os ingressos' };

    // Ingresso do próprio Whizr leva à página do evento (checkout); `link_ticket`
    // é a venda do produtor em outra plataforma e abre em nova aba.
    const hasInternalTickets = hasTickets || hasProducts;
    const externalTicketLink = content.link_ticket;
    const showFooter = !isPast && (hasInternalTickets || !!externalTicketLink);
    const isExternalTicket = !hasInternalTickets && !!externalTicketLink;
    const ticketHref = hasInternalTickets ? eventUrl : externalTicketLink;
    const ctaLabel = hasTickets && hasProducts
        ? 'Ingressos e produtos'
        : hasTickets
            ? 'Comprar ingresso'
            : hasProducts ? 'Garantir produtos' : 'Garantir ingresso';

    const firstDate = content.dates?.[0];
    const firstStart = shortTime(firstDate?.start);
    const firstEnd = shortTime(firstDate?.end);
    const timeNote = firstStart
        ? (firstEnd ? `${firstStart} às ${firstEnd}` : `A partir das ${firstStart}`)
        : undefined;

    const tagLine = content.tags?.map(tag => tag.name).join(' • ');
    const organizers = buildOrganizers(content);
    const address = fullAddress(content.address);
    const showAddress = !content.hide_address;
    const directions = directionsUrl(content, address);
    const calendarLocation = showAddress ? [venueName(content), address].filter(Boolean).join(' - ') : undefined;
    const upcomingDay = nextCalendarDay(content.dates);
    const cover = content.images?.[0] || PLACEHOLDER;

    const toCalendar = day => calendarUrl({
        title: content.name,
        day,
        location: calendarLocation,
        details: eventUrl,
    });

    return (
        <Container>
            <Hero>
                {/* Cartazes costumam ser verticais: com `cover` perdiam justamente
                    o texto impresso. A arte aparece inteira sobre uma cópia
                    borrada dela mesma, que preenche a faixa. */}
                <HeroBlur aria-hidden='true' src={cover} />
                <HeroImage
                    src={cover}
                    alt={`Cartaz do evento ${content.name}`}
                    onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER; }}
                />
                <HeroScrim />

                <TopButtons>
                    {onClose ? (
                        <OverlayButton onClick={onClose} aria-label='Fechar'>
                            <X size={18} />
                        </OverlayButton>
                    ) : <span />}

                    <TopGroup>
                        <OverlayButton
                            onClick={handleFavorite}
                            aria-pressed={isFavorited}
                            aria-label={isFavorited ? 'Remover dos favoritos' : 'Favoritar evento'}
                        >
                            <Heart
                                size={18}
                                color={isFavorited ? colors.occasionalPink : ui.ink}
                                fill={isFavorited ? colors.occasionalPink : 'none'}
                            />
                        </OverlayButton>
                        <OverlayButton onClick={handleShare} aria-label={linkCopied ? 'Link copiado' : 'Compartilhar evento'}>
                            {linkCopied ? <Check size={18} color={colors.successDark} /> : <Share2 size={18} />}
                        </OverlayButton>
                    </TopGroup>
                </TopButtons>

                {linkCopied && <CopiedToast role='status'>Link copiado</CopiedToast>}
            </Hero>

            <Sheet>
                <Title>{content.name}</Title>

                {!!tagLine && <TagLine $hasOrganizers={organizers.length > 0}>{tagLine}</TagLine>}

                {organizers.length > 0 && (
                    <OrganizedBy>
                        <Avatars>
                            {organizers.slice(0, 3).map((organizer, index) => (
                                <AvatarSlot key={organizer.key} $first={index === 0}>
                                    <DynamicProfileImage size='24px' img={organizer.photo} word={organizer.name} textSize='normal' />
                                </AvatarSlot>
                            ))}
                        </Avatars>
                        <span>
                            Organizado por <strong>{joinNames(organizers.map(item => item.name))}</strong>
                        </span>
                    </OrganizedBy>
                )}

                <Facts>
                    <Fact
                        icon={<Calendar size={18} />}
                        title={firstDate?.date ? shortDate(firstDate.date) : 'Data a definir'}
                        note={timeNote}
                    />
                    {showAddress && (
                        <Fact icon={<MapPin size={18} />} title={venueName(content)} note={venueCity(content) || undefined} />
                    )}
                    <Fact icon={<Ticket size={18} />} title={priceFact.title} note={priceFact.note} />
                </Facts>

                {!!upcomingDay && (
                    <Actions>
                        <OutlineButton href={toCalendar(upcomingDay)} target='_blank' rel='noopener noreferrer'>
                            <CalendarPlus size={15} />
                            Adicionar à agenda
                        </OutlineButton>
                    </Actions>
                )}

                <Chips>
                    {!!content.classification?.name && (
                        <Chip>
                            {isFree ? <Gift size={13} /> : <CreditCard size={13} />}
                            {isFree ? eventTypes.free.label : eventTypes.paid.label}
                        </Chip>
                    )}
                    {!!content.adulthood && (
                        <Chip>
                            <Info size={13} />
                            +18
                        </Chip>
                    )}
                    {content.origin === AI_ORIGIN && (
                        <Chip
                            as='button'
                            $clickable
                            type='button'
                            onClick={() => setShowAiNote(open => !open)}
                            aria-expanded={showAiNote}
                        >
                            <Zap size={13} />
                            Mapeado com IA
                            <Info size={12} color={ui.inkSoft} />
                        </Chip>
                    )}
                </Chips>

                {showAiNote && (
                    <AiNote>
                        O Whizr usa inteligência artificial para mapear eventos que não são cadastrados
                        por parceiros oficiais. Sempre confira as informações no canal oficial do evento.
                    </AiNote>
                )}

                {!!(content.description_with_html || content.description) && (
                    <Section title='Sobre o evento'>
                        <DescriptionBox
                            ref={descriptionRef}
                            $clamped={descriptionLong && !descriptionOpen}
                        >
                            {content.description_with_html
                                ? <DescriptionWithHTML data={content.description_with_html} />
                                : <p>{content.description}</p>}
                        </DescriptionBox>
                        {descriptionLong && (
                            <LinkButton type='button' onClick={() => setDescriptionOpen(open => !open)}>
                                {descriptionOpen ? 'Mostrar menos' : 'Ler mais'}
                            </LinkButton>
                        )}
                    </Section>
                )}

                {content.dates?.length > 1 && (
                    <Section title='Datas'>
                        <DateStrip>
                            {content.dates.map((item, index) => (
                                <DateCard
                                    key={`${item.date}-${index}`}
                                    href={toCalendar(item)}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    title='Adicionar à agenda'
                                >
                                    <DateDay>{dayjs(item.date).date()}</DateDay>
                                    <DateLabel>{shortDate(item.date).split(', ')[0]}, {MONTHS[dayjs(item.date).month()]}</DateLabel>
                                    {!!item.start && (
                                        <DateTime>
                                            {shortTime(item.start)}{item.end ? `–${shortTime(item.end)}` : ''}
                                        </DateTime>
                                    )}
                                </DateCard>
                            ))}
                        </DateStrip>
                    </Section>
                )}

                {showAddress && !!address && (
                    <Section title='Local'>
                        <InfoCard
                            as={directions ? 'a' : 'div'}
                            href={directions || undefined}
                            target={directions ? '_blank' : undefined}
                            rel={directions ? 'noopener noreferrer' : undefined}
                            $interactive={!!directions}
                        >
                            <IconDisc>
                                <MapPin size={18} />
                            </IconDisc>
                            <InfoText>
                                <strong>{venueName(content)}</strong>
                                <span>{address}</span>
                            </InfoText>
                            {!!directions && <Navigation size={16} color={ui.link} />}
                        </InfoCard>
                    </Section>
                )}

                {organizers.length > 0 && (
                    <Section title='Quem organiza'>
                        <OrganizerList>
                            {organizers.map(organizer => (
                                <InfoCard
                                    key={organizer.key}
                                    as='a'
                                    href={organizer.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    $interactive
                                >
                                    <DynamicProfileImage size='40px' img={organizer.photo} word={organizer.name} textSize='normal' />
                                    <InfoText>
                                        <strong>{organizer.name}</strong>
                                        <span>{organizer.role}</span>
                                    </InfoText>
                                    <ChevronRight size={18} color={ui.inkFaint} />
                                </InfoCard>
                            ))}
                        </OrganizerList>
                    </Section>
                )}
            </Sheet>

            {showFooter && (
                <Footer>
                    <FooterRow>
                        {(showsFree || lowestPrice != null) && (
                            <PriceBlock>
                                {!showsFree && <small>A partir de</small>}
                                <strong>{showsFree ? 'Gratuito' : currency.format(lowestPrice)}</strong>
                            </PriceBlock>
                        )}

                        <CtaButton
                            href={ticketHref}
                            /* Link de parceiro abre em nova aba; a página do próprio
                               evento navega na mesma. */
                            target={isExternalTicket ? '_blank' : undefined}
                            rel={isExternalTicket ? 'noopener noreferrer' : undefined}
                        >
                            <Ticket size={18} />
                            {ctaLabel}
                        </CtaButton>
                    </FooterRow>

                    {isExternalTicket && (
                        <FooterNote>Você será direcionado para o site de vendas do organizador</FooterNote>
                    )}
                </Footer>
            )}
        </Container>
    );
}

/** "A", "A e B" ou "A, B e C". */
function joinNames(names) {
    if (names.length <= 1) return names.join('');

    return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
}

function Fact({ icon, title, note }) {
    return (
        <FactItem>
            <FactIcon>{icon}</FactIcon>
            <FactTitle>{title}</FactTitle>
            {!!note && <FactNote>{note}</FactNote>}
        </FactItem>
    );
}

function Section({ title, children }) {
    return (
        <SectionBox>
            <SectionTitle>{title}</SectionTitle>
            {children}
        </SectionBox>
    );
}

/** Mesma silhueta da modal carregada, para ela não pular quando o evento chega. */
function DetailSkeleton({ onClose }) {
    return (
        <Container aria-busy='true' aria-label='Carregando evento'>
            <Hero $skeleton>
                {onClose && (
                    <TopButtons>
                        <OverlayButton onClick={onClose} aria-label='Fechar'>
                            <X size={18} />
                        </OverlayButton>
                    </TopButtons>
                )}
            </Hero>
            <Sheet>
                <Bone width='75%' height='24px' />
                <Bone width='45%' height='14px' marginTop='10px' />
                <Bone width='100%' height='86px' marginTop='20px' radius='16px' />
                <Bone width='92%' height='14px' marginTop='24px' />
                <Bone width='85%' height='14px' marginTop='10px' />
                <Bone width='60%' height='14px' marginTop='10px' />
            </Sheet>
        </Container>
    );
}

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
    from { background-position: 200% 0; }
    to { background-position: -200% 0; }
`;

const Container = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    text-align: left;
    color: ${ui.ink};
    background: ${colors.neutralLight};
`;

const Hero = styled.div`
    position: relative;
    flex-shrink: 0;
    height: 250px;
    overflow: hidden;
    background: ${ui.ink};

    @media screen and (min-width: ${breakpoints.md}) {
        height: 300px;
    }

    ${({ $skeleton }) => $skeleton && css`
        background: linear-gradient(90deg, #EEECF4 0%, #F7F6FB 50%, #EEECF4 100%);
        background-size: 200% 100%;
        animation: ${shimmer} 1.4s linear infinite;
    `}
`;

const HeroBlur = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: blur(22px) brightness(0.7);
    transform: scale(1.2);
`;

const HeroImage = styled.img`
    position: relative;
    z-index: 1;
    display: block;
    height: 100%;
    max-width: 100%;
    margin: 0 auto;
    object-fit: contain;
`;

// Só o bastante para os botões e a borda do sheet não sumirem numa arte clara.
const HeroScrim = styled.div`
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background: linear-gradient(
        to bottom,
        rgba(10, 5, 25, 0.28) 0%,
        rgba(10, 5, 25, 0) 40%,
        rgba(10, 5, 25, 0) 60%,
        rgba(10, 5, 25, 0.45) 100%
    );
`;

const TopButtons = styled.div`
    position: absolute;
    top: 14px;
    left: 16px;
    right: 16px;
    z-index: 3;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const TopGroup = styled.div`
    display: flex;
    gap: 10px;
`;

const OverlayButton = styled.button`
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${ui.ink};
    background: ${ui.overlayButton};
    box-shadow: 0 2px 10px rgba(10, 5, 25, 0.18);
    transition: transform 0.15s ease, background 0.15s ease;

    &:hover {
        background: #FFFFFF;
    }

    &:active {
        transform: scale(0.94);
    }

    &:focus-visible {
        outline: 2px solid ${ui.link};
        outline-offset: 2px;
    }
`;

const CopiedToast = styled.div`
    position: absolute;
    top: 62px;
    right: 16px;
    z-index: 3;
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: #FFFFFF;
    background: rgba(21, 19, 42, 0.85);
    animation: ${fadeUp} 0.2s ease both;
`;

const Sheet = styled.div`
    position: relative;
    z-index: 4;
    margin-top: -22px;
    padding: 22px 20px 24px;
    border-radius: 26px 26px 0 0;
    background: ${colors.neutralLight};
    animation: ${fadeUp} 0.28s cubic-bezier(0.32, 0.72, 0, 1) both;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 24px 26px 28px;
    }
`;

const Title = styled.h2`
    margin: 0 0 4px;
    font-size: 22px;
    line-height: 1.25;
    font-weight: 700;
    letter-spacing: -0.015em;
    color: ${ui.ink};

    @media screen and (min-width: ${breakpoints.md}) {
        font-size: 24px;
    }
`;

const TagLine = styled.p`
    margin: 0 0 ${({ $hasOrganizers }) => $hasOrganizers ? '10px' : '16px'};
    padding: 0;
    font-size: 13px;
    line-height: 1.4;
    color: ${ui.inkSoft};
    text-transform: capitalize;
`;

const OrganizedBy = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0 16px;
    font-size: 13px;
    line-height: 1.35;
    color: ${ui.inkSoft};

    strong {
        font-weight: 700;
        color: ${ui.ink};
    }
`;

const Avatars = styled.div`
    display: flex;
    flex-shrink: 0;
`;

const AvatarSlot = styled.div`
    margin-left: ${({ $first }) => $first ? '0' : '-8px'};
    border-radius: 50%;
    box-shadow: 0 0 0 2px ${colors.neutralLight};
`;

const Facts = styled.div`
    display: flex;
    padding: 14px 6px;
    margin-bottom: 14px;
    border-radius: 16px;
    background: ${ui.surfaceMuted};
`;

const FactItem = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 6px;
    text-align: center;

    & + & {
        border-left: 1px solid ${ui.border};
    }
`;

const FactIcon = styled.span`
    display: inline-flex;
    color: ${ui.link};
`;

const FactTitle = styled.strong`
    margin-top: 6px;
    font-size: 12.5px;
    line-height: 1.3;
    font-weight: 700;
    color: ${ui.ink};
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const FactNote = styled.span`
    max-width: 100%;
    margin-top: 2px;
    font-size: 11px;
    line-height: 1.3;
    color: ${ui.inkSoft};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 14px;
`;

const OutlineButton = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 999px;
    border: 1px solid ${ui.border};
    font-size: 13px;
    font-weight: 600;
    color: ${ui.ink};
    text-decoration: none;
    transition: border-color 0.15s ease, background 0.15s ease;

    & > svg {
        color: ${ui.link};
    }

    &:hover {
        border-color: ${ui.link};
        background: ${ui.tagBg};
    }
`;

const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 18px;

    &:empty {
        display: none;
    }
`;

const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 11px;
    border-radius: 999px;
    border: none;
    font: inherit;
    font-size: 12.5px;
    font-weight: 700;
    color: ${ui.ink};
    background: ${ui.surfaceMuted};

    ${({ $clickable }) => $clickable && css`
        cursor: pointer;

        &:hover {
            background: ${ui.tagBg};
        }
    `}
`;

const AiNote = styled.p`
    margin: -6px 0 18px;
    padding: 12px 14px;
    border-radius: 12px;
    font-size: 12.5px;
    line-height: 1.5;
    color: ${ui.inkSoft};
    background: ${ui.surfaceMuted};
    animation: ${fadeUp} 0.18s ease both;
`;

const SectionBox = styled.section`
    margin-bottom: 22px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const SectionTitle = styled.h3`
    margin: 0 0 12px;
    font-size: 16px;
    line-height: 1.3;
    font-weight: 700;
    color: ${ui.ink};
`;

const DescriptionBox = styled.div`
    position: relative;
    font-size: 14px;
    line-height: 1.6;
    color: ${ui.inkSoft};
    overflow-wrap: anywhere;

    p {
        margin: 0 0 8px;
    }

    /* O editor do painel deixa o produtor usar títulos grandes; dentro da
       modal eles não podem passar do título do próprio evento. */
    h1, h2, h3, h4 {
        margin: 16px 0 6px;
        font-size: 15px;
        line-height: 1.35;
        font-weight: 700;
        color: ${ui.ink};
    }

    h1:first-child, h2:first-child, h3:first-child, h4:first-child {
        margin-top: 0;
    }

    ul, ol {
        margin: 0 0 8px;
        padding-left: 20px;
    }

    li {
        margin-bottom: 4px;
    }

    ${({ $clamped }) => $clamped && css`
        max-height: ${DESCRIPTION_CLAMP}px;
        overflow: hidden;

        &::after {
            content: '';
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            height: 64px;
            background: linear-gradient(to bottom, rgba(255, 255, 255, 0), ${colors.neutralLight});
        }
    `}
`;

const LinkButton = styled.button`
    margin-top: 6px;
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    font-size: 13.5px;
    font-weight: 700;
    color: ${ui.link};
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
`;

const DateStrip = styled.div`
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding-bottom: 4px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const DateCard = styled.a`
    flex: 0 0 84px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px 6px;
    border-radius: 16px;
    text-decoration: none;
    background: ${ui.surfaceMuted};
    transition: background 0.15s ease;

    &:hover {
        background: ${ui.tagBg};
    }
`;

const DateDay = styled.strong`
    font-size: 22px;
    line-height: 1.2;
    font-weight: 700;
    color: ${ui.link};
`;

const DateLabel = styled.span`
    font-size: 12px;
    font-weight: 700;
    color: ${ui.ink};
`;

const DateTime = styled.span`
    margin-top: 2px;
    font-size: 10.5px;
    color: ${ui.inkSoft};
`;

const InfoCard = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px;
    border-radius: 16px;
    text-decoration: none;
    color: inherit;
    background: ${ui.surfaceMuted};
    transition: background 0.15s ease;

    ${({ $interactive }) => $interactive && css`
        cursor: pointer;

        &:hover {
            background: ${ui.tagBg};
        }
    `}
`;

const IconDisc = styled.span`
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${ui.link};
    background: ${ui.tagBg};
`;

const InfoText = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;

    strong {
        font-size: 14px;
        font-weight: 700;
        color: ${ui.ink};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    span {
        font-size: 12.5px;
        line-height: 1.4;
        color: ${ui.inkSoft};
    }
`;

const OrganizerList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Footer = styled.div`
    position: sticky;
    bottom: 0;
    z-index: 10;
    padding: 14px 20px;
    border-top: 1px solid ${ui.border};
    background: ${colors.neutralLight};

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 14px 26px;
    }

    @media screen and (max-width: 899px) {
        padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 14px);
    }
`;

const FooterRow = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

const PriceBlock = styled.div`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;

    small {
        font-size: 11px;
        font-weight: 600;
        color: ${ui.inkSoft};
    }

    strong {
        font-size: 17px;
        line-height: 1.3;
        font-weight: 700;
        color: ${ui.ink};
        white-space: nowrap;
    }
`;

const CtaButton = styled.a`
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 48px;
    padding: 0 18px;
    border-radius: 14px;
    font-size: 15px;
    font-weight: 700;
    color: #FFFFFF;
    text-decoration: none;
    background: ${ui.cta};
    box-shadow: 0 6px 18px rgba(105, 12, 228, 0.28);
    transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;

    &:hover {
        color: #FFFFFF;
        filter: brightness(1.06);
        box-shadow: 0 8px 22px rgba(105, 12, 228, 0.34);
    }

    &:active {
        transform: scale(0.98);
    }

    &:focus-visible {
        outline: 2px solid ${ui.link};
        outline-offset: 2px;
    }
`;

const FooterNote = styled.p`
    margin: 8px 0 0;
    font-size: 11px;
    text-align: center;
    color: ${ui.inkFaint};
`;

const Bone = styled.div`
    width: ${({ width }) => width};
    height: ${({ height }) => height};
    margin-top: ${({ marginTop }) => marginTop || 0};
    border-radius: ${({ radius }) => radius || '8px'};
    background: linear-gradient(90deg, #EEECF4 0%, #F7F6FB 50%, #EEECF4 100%);
    background-size: 200% 100%;
    animation: ${shimmer} 1.4s linear infinite;
`;
